import React, { useState } from 'react';
import {
  X,
  Save,
  Trash2,
  Sparkles,
  Calendar,
  Tag,
  Info,
  ExternalLink,
  Loader2,
  User,
  FileText,
  Map,
  Image as ImageIcon,
  Check,
  Copy,
  Globe,
  Link2,
  AlignLeft,
} from 'lucide-react';
import { AssetMetadata } from '@linein/shared';
import { apiService } from '../services/api';

type UrlFieldType = 'THUMB' | 'HD' | 'RAW';

interface EditModalProps {
  asset: AssetMetadata;
  onClose: () => void;
  onUpdate: (asset: AssetMetadata) => void;
  onDelete: (id: string) => void;
  onToast: (message: string) => void;
}

const Label = ({ children, icon: Icon }: { children?: React.ReactNode; icon?: any }) => (
  <div className="flex items-center gap-1.5 mb-1">
    {Icon && <Icon size={12} className="text-slate-400" />}
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
      {children}
    </label>
  </div>
);

const CopyField = ({
  label,
  value,
  type,
  loading,
  onRequestAndCopy,
  onNotify,
}: {
  label: string;
  value: string;
  type: UrlFieldType;
  loading: boolean;
  onRequestAndCopy: (type: UrlFieldType) => Promise<void>;
  onNotify: (message: string) => void;
}) => {
  const [copied, setCopied] = useState(false);
  const hasPublicUrl = /^https?:\/\//i.test(value || '');

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    onNotify('已複製網址');
    setTimeout(() => setCopied(false), 2000);
  };

  const colors = {
    THUMB: 'bg-blue-50 text-blue-600 border-blue-100',
    HD: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    RAW: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  };

  return (
    <div className="flex items-center gap-2 mb-2 group">
      <div
        className={`px-2 py-1 rounded text-[9px] font-black uppercase border ${colors[type]} w-16 text-center shrink-0`}
      >
        {label}
      </div>
      {hasPublicUrl ? (
        <>
          <div className="flex-1 font-mono text-[10px] text-slate-400 truncate bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
            {value}
          </div>
          <button
            onClick={handleCopy}
            className={`p-1.5 rounded-lg transition-all ${copied ? 'bg-green-500 text-white' : 'hover:bg-slate-100 text-slate-400'}`}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </>
      ) : (
        <button
          onClick={() => onRequestAndCopy(type)}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-1.5 font-bold text-[10px] text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-1.5 hover:bg-indigo-100 disabled:opacity-60"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
          {loading ? '取得中...' : '取得檔案並複製網址'}
        </button>
      )}
    </div>
  );
};

const EditModal: React.FC<EditModalProps> = ({ asset, onClose, onUpdate, onDelete, onToast }) => {
  const [formData, setFormData] = useState<AssetMetadata>({ ...asset });
  const [activeTab, setActiveTab] = useState<'PROPERTIES' | 'DETAILS'>('PROPERTIES');
  const [resolvingUrlType, setResolvingUrlType] = useState<UrlFieldType | null>(null);
  const [loadingField, setLoadingField] = useState<
    'tags' | 'activityTags' | 'assetTypeTags' | 'description' | null
  >(null);

  const handleRequestAndCopyUrl = async (type: UrlFieldType) => {
    setResolvingUrlType(type);
    try {
      const urls = await apiService.promoteAssetToGcpFull(formData.id);

      const nextData: AssetMetadata = {
        ...formData,
        thumbnailUrl: urls.thumbnailUrl || formData.thumbnailUrl,
        hdUrl: urls.hdUrl || formData.hdUrl,
        originalUrl: urls.originalUrl || formData.originalUrl,
      };
      setFormData(nextData);

      const targetUrl =
        type === 'THUMB'
          ? nextData.thumbnailUrl
          : type === 'HD'
            ? nextData.hdUrl
            : nextData.originalUrl;

      if (!targetUrl) {
        onToast('取得失敗：未回傳可複製網址');
        return;
      }

      await navigator.clipboard.writeText(targetUrl);
      onToast(/^https?:\/\//i.test(targetUrl) ? '已取得並複製公開網址' : '已複製來源路徑');
    } catch (err) {
      console.error('Request and copy URL failed:', err);
      onToast('取得檔案失敗');
    } finally {
      setResolvingUrlType(null);
    }
  };

  const handleFetchAISuggestions = async (field: 'tags' | 'activityTags' | 'assetTypeTags') => {
    if (asset.sourceType !== 'IMAGE' || !asset.thumbnailUrl) return;
    setLoadingField(field);
    try {
      // Use backend AI API with thumbnailUrl
      const result = await apiService.analyzeImage(asset.thumbnailUrl);

      if (field === 'tags')
        setFormData((prev) => ({
          ...prev,
          tags: Array.from(new Set([...prev.tags, ...result.tags])),
        }));
      if (field === 'activityTags')
        setFormData((prev) => ({
          ...prev,
          activityTags: Array.from(new Set([...prev.activityTags, ...result.activityNature])),
        }));
      if (field === 'assetTypeTags')
        setFormData((prev) => ({
          ...prev,
          assetTypeTags: Array.from(new Set([...prev.assetTypeTags, ...result.assetNature])),
        }));
    } catch (err) {
      console.error('AI analysis failed:', err);
    } finally {
      setLoadingField(null);
    }
  };

  const handleFetchDescriptionSuggestion = async () => {
    if (asset.sourceType !== 'IMAGE' || !asset.thumbnailUrl) return;
    setLoadingField('description');
    try {
      const result = await apiService.analyzeImage(asset.thumbnailUrl);
      setFormData((prev) => ({
        ...prev,
        description: result.description || prev.description,
      }));
    } catch (err) {
      console.error('AI description generation failed:', err);
    } finally {
      setLoadingField(null);
    }
  };

  const handleSave = () => {
    onUpdate({ ...formData, modifiedDate: new Date().toISOString() });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-[2rem] w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/30">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-900 text-white rounded-lg flex items-center justify-center shadow-lg">
              <FileText size={16} />
            </div>
            <h2 className="text-base font-black text-slate-900">素材資源配置</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDelete(asset.id)}
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
            >
              <Trash2 size={16} />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg transition-all">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
          <div className="w-full lg:w-[40%] bg-slate-950 flex flex-col border-r border-slate-100 p-6 relative">
            <div className="flex-1 relative flex items-center justify-center min-h-[200px]">
              <img
                src={asset.thumbnailUrl}
                className="max-w-full max-h-full rounded-xl shadow-2xl object-contain ring-1 ring-white/10"
                alt="Preview"
              />
              {asset.externalUrl && (
                <a
                  href={asset.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-4 right-4 p-2 bg-white/10 backdrop-blur text-white rounded-lg hover:bg-white/20 transition-all"
                >
                  <ExternalLink size={16} />
                </a>
              )}
            </div>

            <div className="mt-6 space-y-1 bg-white/5 p-4 rounded-xl backdrop-blur-sm border border-white/5">
              <p className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-3">
                影像鏈結複製
              </p>
              <CopyField
                label="縮圖"
                value={formData.thumbnailUrl}
                type="THUMB"
                loading={resolvingUrlType === 'THUMB'}
                onRequestAndCopy={handleRequestAndCopyUrl}
                onNotify={onToast}
              />
              <CopyField
                label="高清"
                value={formData.hdUrl}
                type="HD"
                loading={resolvingUrlType === 'HD'}
                onRequestAndCopy={handleRequestAndCopyUrl}
                onNotify={onToast}
              />
              <CopyField
                label="原始"
                value={formData.originalUrl}
                type="RAW"
                loading={resolvingUrlType === 'RAW'}
                onRequestAndCopy={handleRequestAndCopyUrl}
                onNotify={onToast}
              />
            </div>
          </div>

          <div className="flex-1 flex flex-col min-w-0 bg-white">
            <div className="flex border-b border-slate-100 p-2 gap-2 shrink-0 bg-slate-50/50">
              <button
                onClick={() => setActiveTab('PROPERTIES')}
                className={`flex-1 py-2 px-4 rounded-xl text-xs font-black transition-all ${activeTab === 'PROPERTIES' ? 'bg-white text-indigo-600 shadow-sm border border-indigo-100' : 'text-slate-500 hover:bg-white/50'}`}
              >
                屬性
              </button>
              <button
                onClick={() => setActiveTab('DETAILS')}
                className={`flex-1 py-2 px-4 rounded-xl text-xs font-black transition-all ${activeTab === 'DETAILS' ? 'bg-white text-indigo-600 shadow-sm border border-indigo-100' : 'text-slate-500 hover:bg-white/50'}`}
              >
                詳細
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6">
              {activeTab === 'PROPERTIES' && (
                <div className="space-y-6 animate-in slide-in-from-right-2 duration-300">
                  <div className="grid grid-cols-1 gap-5">
                    <div>
                      <Label icon={FileText}>名稱</Label>
                      <input
                        type="text"
                        value={formData.originalFilename}
                        onChange={(e) =>
                          setFormData({ ...formData, originalFilename: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none font-bold text-slate-800 text-sm transition-all"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label icon={AlignLeft}>描述</Label>
                        <button
                          onClick={handleFetchDescriptionSuggestion}
                          disabled={loadingField === 'description'}
                          className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-md text-[9px] font-black hover:bg-indigo-100 disabled:opacity-50 transition-all"
                        >
                          {loadingField === 'description' ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <Sparkles size={10} />
                          )}{' '}
                          AI 建議
                        </button>
                      </div>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none font-medium text-slate-800 text-sm transition-all min-h-[100px] resize-none"
                        placeholder="輸入素材的詳細描述..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <Label icon={FileText}>資料來源</Label>
                        <input
                          type="text"
                          value={formData.sourceType === 'IMAGE' ? '圖片' : formData.sourceType === 'VIDEO' ? '影片' : formData.sourceType}
                          readOnly
                          className="w-full px-4 py-2.5 bg-slate-100 border border-slate-100 rounded-xl focus:ring-2 focus:ring-indigo-500/10 outline-none font-bold text-sm text-slate-600"
                        />
                      </div>
                      <div>
                        <Label icon={Map}>處擬路徑</Label>
                        <input
                          type="text"
                          value={formData.folderPath || '/'}
                          onChange={(e) => setFormData({ ...formData, folderPath: e.target.value })}
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-indigo-500/10 outline-none font-bold text-sm"
                          placeholder="/資料夾/子資料夾"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <Label icon={Map}>活動 ID</Label>
                        <input
                          type="text"
                          value={formData.activityId}
                          onChange={(e) => setFormData({ ...formData, activityId: e.target.value })}
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-indigo-500/10 outline-none font-bold text-sm"
                        />
                      </div>
                      <div>
                        <Label icon={User}>上傳者</Label>
                        <input
                          type="text"
                          value={formData.uploader}
                          onChange={(e) => setFormData({ ...formData, uploader: e.target.value })}
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-indigo-500/10 outline-none font-bold text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t border-slate-50">
                    <section>
                      <div className="flex items-center justify-between mb-2">
                        <Label icon={Tag}>標籤 Hashtags</Label>
                        <button
                          onClick={() => handleFetchAISuggestions('tags')}
                          disabled={loadingField === 'tags'}
                          className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-md text-[9px] font-black hover:bg-indigo-100 disabled:opacity-50 transition-all"
                        >
                          {loadingField === 'tags' ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <Sparkles size={10} />
                          )}{' '}
                          AI 建議
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 p-3 bg-slate-50 rounded-xl border border-slate-100 min-h-[60px]">
                        {formData.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="px-2.5 py-1 bg-white text-blue-600 rounded-lg text-[11px] font-bold border border-blue-50 flex items-center gap-1.5"
                          >
                            #{tag}{' '}
                            <button
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  tags: formData.tags.filter((_, idx) => idx !== i),
                                })
                              }
                              className="text-slate-300 hover:text-red-500"
                            >
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                        <button className="px-2.5 py-1 border border-dashed border-slate-300 text-slate-400 rounded-lg text-[11px] font-bold hover:border-indigo-400 hover:text-indigo-600 transition-all">
                          + 新增
                        </button>
                      </div>
                    </section>
                  </div>
                </div>
              )}

              {activeTab === 'DETAILS' && (
                <div className="space-y-4 animate-in slide-in-from-left-2 duration-300">
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-3">
                    <div>
                      <Label icon={FileText}>原始檔名</Label>
                      <p className="text-slate-800 font-bold text-sm">{asset.originalFilename}</p>
                    </div>
                    <div>
                      <Label icon={Globe}>原始路徑</Label>
                      <input
                        type="text"
                        value={formData.sourcePath || ''}
                        onChange={(e) => setFormData({ ...formData, sourcePath: e.target.value })}
                        placeholder="例如: photos/2024/vacation/beach.jpg"
                        className="w-full px-3 py-2 text-sm font-mono border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-slate-400 text-xs mt-1">用於識別檔案在原本本地資料夾的位置</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl">
                      <Label icon={ImageIcon}>解析度</Label>
                      <p className="text-slate-800 font-mono font-black text-sm">
                        {formData.resolution || '未知'}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl">
                      <Label icon={Calendar}>修改時間</Label>
                      <p className="text-slate-800 font-mono font-black text-sm">
                        {formData.modifiedDate ? new Date(formData.modifiedDate).toLocaleString('zh-TW') : '未知'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl">
                      <Label icon={FileText}>資料來源</Label>
                      <p className="text-slate-800 font-bold text-sm">
                        {formData.sourceType === 'IMAGE' ? '圖片' : formData.sourceType === 'VIDEO' ? '影片' : formData.sourceType}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl">
                      <Label icon={Calendar}>上傳時間</Label>
                      <p className="text-slate-800 font-mono font-black text-sm">
                        {new Date(formData.uploadDate).toLocaleString('zh-TW')}
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl">
                    <Label icon={Link2}>外部連結 / 源路徑</Label>
                    <p className="text-slate-500 font-mono text-[10px] break-all">
                      {formData.originalUrl}
                    </p>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                    <Label icon={Info}>中繼資料摘要</Label>
                    <p className="text-amber-800 text-[11px] font-bold leading-relaxed">
                      此素材存儲於冷儲存級別的 Bucket。訪問高清與原始圖片可能會有 1-2 秒的延遲。
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-100 flex justify-end gap-3 bg-slate-50 shrink-0">
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-200 rounded-xl transition-all text-sm"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-8 py-2.5 bg-slate-900 text-white font-black rounded-xl shadow-xl hover:bg-slate-800 transition-all flex items-center gap-2 text-sm"
              >
                <Save size={16} /> 儲存
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditModal;
